/* eslint-disable react/prop-types */
import React, { useState, useRef } from 'react';
import classNames from 'classnames';
import { stub as $t } from '@nbcnews/analytics-framework';
import { Carousel } from 'components/Carousel';
import { Save } from 'components/SocialShareMenu/Save';
import Link from 'components/Link';
import { Picture } from 'components/Picture';
import { Flag } from '../Product/Flag';
import { Badge } from '../Product/Badge';
import { CarouselPagination } from './CarouselPagination';
import styles from './styles.module.scss';

/**
 * Render carousel picture
 * @param {object} props
 * @param {object} props.media
 * @param {string} props.className
 * @param {string} props.link
 * @param {object} props.product
 * @param {string} props.flavor
 * @param {object} props.sizes
 * @param {string} props.altText
 * @param {string} props.contentId
 * @param {string} props.contentType
 * @param {Function} props.onClick
 * @param {number} props.index
 * @param {string} props.badgeAlt
 * @param {string} props.badgeUrl
 */
const CarouselPicture = ({
  media,
  className,
  link,
  product,
  flavor,
  sizes,
  altText,
  contentId,
  contentType,
  onClick,
  index,
  badgeAlt,
  badgeUrl,
}) => {
  const mediaUrl = media?.url?.primary;
  const mediaWidth = media?.width;
  const mediaHeight = media?.height;

  if (!mediaUrl) return null;

  // Only want to use data-product-id if this is used in a product context
  const {
    id: productId,
  } = product ?? {};

  const hasBadge = badgeUrl && badgeAlt;

  return (
    <div className={classNames(className, { [styles['picture-carousel__first-picture']]: index === 0 })}>
      {hasBadge && index === 0 && (<Badge src={badgeUrl} alt={badgeAlt} />)}
      {link ? (
        <Link
          className="w-100"
          href={link}
          rel="sponsored noopener noreferrer"
          data-testid="picture-carousel-link"
          target="_blank"
          data-product-id={productId}
          tabIndex="-1"
          onClick={onClick}
        >
          <Picture
            alt={altText}
            flavor={flavor}
            lazyloaded
            originalHeight={mediaHeight}
            originalWidth={mediaWidth}
            responsiveFlavors={sizes}
            url={mediaUrl}
          />
        </Link>
      ) : (
        <Picture
          alt={altText}
          flavor={flavor}
          lazyloaded
          originalHeight={mediaHeight}
          originalWidth={mediaWidth}
          responsiveFlavors={sizes}
          url={mediaUrl}
        />
      )}
      <Save
        contentId={contentId}
        contentType={contentType}
        options={{ isThumbnail: true, showCTA: true }}
      />
    </div>
  );
};

/**
 * Picture carousel component
 * @param {object} props
 * @param {object} props.product
 * @param {object} props.media
 * @param {string} props.link
 * @param {string} props.flagName
 * @param {string} props.badgeUrl
 * @param {string} props.badgeAlt
 * @param {string} props.additionalClasses
 * @param {string} props.flavor
 * @param {string} props.size
 * @param {object} props.sizes
 * @param {string} props.altText
 * @param {string} props.contentId
 * @param {string} props.contentType
 * @param {Function} props.onClick
 */
function PictureCarousel({
  product,
  media,
  link,
  flagName,
  badgeUrl,
  badgeAlt,
  additionalClasses,
  flavor,
  size,
  sizes,
  altText,
  contentId,
  contentType,
  onClick,
}) {
  const [carouselIndex, setCarouselIndex] = useState(0);
  const actionSourceRef = useRef(null);
  const event = size === 'large' ? 'mbt_spotlightode_action' : 'mbt_producttease_action';

  if (!media || media.length < 1) return null;

  const numberOfPictures = media.length;

  /**
   * Handle carousel tracking event
   * @param {string} action
   * @param {number} newIndex
   */
  const handleCarouselTrackingEvent = (action, newIndex) => {
    $t('track', event, {
      action: `carousel image ${action}`,
      totalImages: numberOfPictures,
      currentImage: newIndex + 1,
    });
  };


  return (
    <div className={styles['picture-carousel__carousel-container']}>
      <div className={additionalClasses} aria-roledescription="carousel">
        {flagName && <Flag text={flagName} />}
        <div role="group" aria-labelledby="carousel-images">
          <Carousel
            cards={media}
            currentCardIndex={carouselIndex}
            onScroll={(newSlideIdx) => {
              if (actionSourceRef.current === 'dot') {
                handleCarouselTrackingEvent('dot', newSlideIdx);
                actionSourceRef.current = null;
              } else if (actionSourceRef.current === 'click') {
                handleCarouselTrackingEvent('click', newSlideIdx);
                actionSourceRef.current = null;
              } else {
                handleCarouselTrackingEvent('swipe', newSlideIdx);
              }
              setCarouselIndex(newSlideIdx);
            }}
            renderCard={(card, index, className) => (
              <CarouselPicture
                key={`carousel-picture-${index + 1}`}
                media={card}
                className={className}
                link={link}
                product={product}
                flavor={flavor}
                sizes={sizes}
                altText={altText}
                contentId={contentId}
                contentType={contentType}
                onClick={onClick}
                index={index}
                badgeAlt={badgeAlt}
                badgeUrl={badgeUrl}
              />
            )}
          />
        </div>
      </div>
      <CarouselPagination
        numberOfPictures={numberOfPictures}
        carouselIndex={carouselIndex}
        setCarouselIndex={setCarouselIndex}
        actionSourceRef={actionSourceRef}
      />
    </div>
  );
}

export { PictureCarousel };
